import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button } from '@mui/material';
import { getIsEmptyObj } from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import FormGenerator from 'app/shared/componentsRestyled/common/formGenerator/FormGenerator';
import ListTableWrapper from 'app/shared/componentsRestyled/common/ListTableWrapper';
import ButtonGroupWrapper from 'app/shared/componentsRestyled/coreDesignSystem/Buttons/ButtonGroupWrapper';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { FeatureFlag } from 'constants/featureFlags';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';

import { openConfirmationDialogAction } from 'store/global/actions';
import { selectIsLoanOptimalBlueOrSifterEngineType } from 'store/loanApplications/selectors';
import { getPriceConcessionConfigurationAction } from 'store/pricing/actions';
import {
  selectExternalLoanLockCustomAdjustmentsOptions,
  selectPriceConcessionAcceptReasons,
} from 'store/pricing/selectors';
import { selectFilteredCustomAdjustmentsOptions } from 'store/programs/selectors';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';

import { customAdjustmentsConfigFields, getCustomAdjustmentsConfig } from './LockInfo.config';
import { CustomAdjustmentType } from './LockInfo.constants';
import { controlsBlock, formPartCustomStyles } from './LockInfo.styles';

const AddCustomAdjustmentsForm = ({
  isLoading,
  cancelForm,
  saveForm,
  customAdjustmentsTypeIds,
  setIsLocalLoading,
}) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const isExternalLoanLockEnabled = useFeatureToggle(FeatureFlag.EnableExternalLoanLock);

  const isOptimalBlueOrSifterEngineType = useSelector(selectIsLoanOptimalBlueOrSifterEngineType);
  const customAdjustmentsOptions = useSelector(selectFilteredCustomAdjustmentsOptions);
  const externalLoanLockOptions = useSelector(selectExternalLoanLockCustomAdjustmentsOptions);
  const acceptReasons = useSelector(selectPriceConcessionAcceptReasons);

  const [isCommentRequired, setIsCommentRequired] = useState(false);

  const isOBExternalLoanLock = isOptimalBlueOrSifterEngineType && isExternalLoanLockEnabled;

  const observableFieldsNames = useMemo(() => {
    return Object.keys(customAdjustmentsConfigFields);
  }, []);

  const filteredCustomAdjustmentsOptions = useMemo(() => {
    const optionsToUse = isOBExternalLoanLock ? externalLoanLockOptions : customAdjustmentsOptions;

    return optionsToUse.filter(({ value }) => !customAdjustmentsTypeIds.includes(value));
  }, [
    isOBExternalLoanLock,
    externalLoanLockOptions,
    customAdjustmentsOptions,
    customAdjustmentsTypeIds,
  ]);

  const {
    initialFormValues,
    schema: initialSchema,
    fieldsConfig,
    config,
  } = useMemo(() => {
    const config = getCustomAdjustmentsConfig({
      customAdjustmentsOptions: filteredCustomAdjustmentsOptions,
      shouldShowComment: isOBExternalLoanLock,
      isCommentRequired,
    });

    const { initialFormValues, schema, fieldsConfig } = getConfigInfo({
      applicationData: {},
      config,
      isNestedForm: true,
    });

    return { initialFormValues, schema, fieldsConfig, config };
  }, [filteredCustomAdjustmentsOptions, isCommentRequired, isOBExternalLoanLock]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const isFormEmpty = getIsEmptyObj(form);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      saveForm({ form, type: CustomAdjustmentType.CUSTOM_ADJUSTMENT });
    },
    [form, validateForm, saveForm],
  );

  const onCancelClick = useCallback(() => {
    if (!isFormChanged) {
      cancelForm();

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm();
        },
      }),
    );
  }, [dispatch, isFormChanged, cancelForm]);

  useEffect(() => {
    setSchema(initialSchema);
  }, [setSchema, initialSchema]);

  useEffect(() => {
    const selectedReasonId = form?.adjustmentReason?.value;

    if (!selectedReasonId || !isOBExternalLoanLock) {
      setIsCommentRequired(false);

      return;
    }

    const selectedReason = acceptReasons?.find(({ id }) => id === selectedReasonId);
    setIsCommentRequired(selectedReason?.isCommentRequired ?? false);
  }, [form, acceptReasons, isOBExternalLoanLock]);

  useEffectSafeStrictMode(() => {
    if (!isOBExternalLoanLock) {
      return;
    }

    setIsLocalLoading(true);
    dispatch(getPriceConcessionConfigurationAction({ applicationId }))
      .catch(Error)
      .finally(() => setIsLocalLoading(false));
  }, [isOBExternalLoanLock, dispatch, setIsLocalLoading, applicationId]);

  return (
    <ListTableWrapper
      customStyles={formPartCustomStyles}
      title="Add Custom Adjustments"
      titleVariant="bold16"
    >
      <form noValidate data-ischanged={isFormChanged} onSubmit={onSubmit}>
        <Box>
          <FormGenerator
            isNestedForm
            config={config}
            form={form}
            formName={FormNames.CUSTOM_ADJUSTMENTS}
            handleChange={handleChange}
            isLoading={isLoading}
            numberOfColumns={1}
            pendingChanges={formPendingChanges}
            setInForm={setInForm}
            staticValidateField={staticValidateField}
            validateField={validateField}
            validationErrors={validationErrors}
          />
          <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT} sx={controlsBlock}>
            <Button
              data-testid={PropertyAndLoanTestIds.CUSTOM_ADJUSTMENTS_FORM_CANCEL_BUTTON}
              size="small"
              variant="outlined"
              onClick={onCancelClick}
            >
              Cancel
            </Button>
            <Button
              data-testid={PropertyAndLoanTestIds.CUSTOM_ADJUSTMENTS_FORM_SUBMIT_BUTTON}
              disabled={!isFormChanged || isFormEmpty}
              size="small"
              type="submit"
            >
              Add
            </Button>
          </ButtonGroupWrapper>
        </Box>
      </form>
    </ListTableWrapper>
  );
};

AddCustomAdjustmentsForm.propTypes = {
  cancelForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  customAdjustmentsTypeIds: PropTypes.array.isRequired,
  setIsLocalLoading: PropTypes.func.isRequired,
  ...WithLoadingPropTypes,
};

export default memo(AddCustomAdjustmentsForm);
